import "./App.scss";

import React, { lazy, Suspense, useEffect } from "react";
import { Route, Routes } from "react-router-dom";

// START: GitHub Copilot Generated Code
// Home and Spinner are kept as eager imports — they are needed on first paint.
import Home from "./components/Home/Home";
import MyMhSpinner from "./components/shared/Spinner/MyMhSpinner";
// END: GitHub Copilot Generated Code
import config from "./config/config";
import { KEY_debug, KEY_sessionLanguage } from "./constants";
import {
  selectHasContentForCurrentLanguage
} from "./reduxToolkit/features/contentSlice";
import { selectLanguage, setLanguage } from "./reduxToolkit/features/languageSlice";
import { useAppDispatch, useAppSelector } from "./reduxToolkit/hooks";
import { toastError } from "./services/appService";
import { isSupportedLanguage } from "./services/languageUtils";
import { readFromStorage } from "./services/storageServies";
import { addQueryParameter, isMobileApp, LogVerbose, redirectToUrl } from "./services/utilities";

import type Client from "fhirclient/Client";
// START: GitHub Copilot Generated Code
/**
 * Helper component for routes that perform external redirects (window.location).
 * Executes the redirect in useEffect and renders fallback content while redirecting.
 * ADA/OWASP: No user-facing input; safe redirect via existing redirectToUrl utility.
 */
const RedirectEffect: React.FC<{
  onMount: () => void;
  children?: React.ReactNode;
}> = ({ onMount, children }) => {
  const hasRedirected = React.useRef(false);
  React.useEffect(() => {
    if (!hasRedirected.current) {
      hasRedirected.current = true;
      onMount();
    }
  }, []);
  return <>{children}</>;
};
// END: GitHub Copilot Generated Code

// All other routes are lazy-loaded so their JS bundles are only downloaded
// when the user navigates to that route, reducing the initial JS payload.
const FunctionIsComing = lazy(() => import("./components/ErrorPage/FunctionIsComing/FunctionIsComing"));
const GetCareNow = lazy(() => import("./components/GetCareNow/GetCareNow"));
const PageNotFound = lazy(() => import("./components/PageNotFound/PageNotFound"));
const PayAsGuest = lazy(() => import("./components/PayBill/PayAsGuest"));
const ScheduledVisits = lazy(() => import("./components/Scheduling/ScheduledVisits"));
const Scheduling = lazy(() => import("./components/Scheduling/Scheduling"));
const SchedulingGuide = lazy(() => import("./components/Scheduling/SchedulingGuide"));
const DevIFramePage = lazy(() => import("./components/shared/PlaceHolderPage/DevIFramePage"));
const PlaceHolderPage = lazy(() => import("./components/shared/PlaceHolderPage/PlaceHolder"));
const UrgentCare = lazy(() => import("./components/UrgentCare/UrgentCare"));
const VirtualUrgentCare = lazy(() => import("./components/VirtualUrgentCare/VirtualUrgentCare"));
const VirtualWaitingRoom = lazy(() => import("./components/VirtualWaitingRoom/VirtualWaitingRoom"));

const App = ({ fhirClient, lang }: { fhirClient?: Client; lang?: string | null; }) => {

  const dispatch = useAppDispatch();
  const currentLanguage = useAppSelector(selectLanguage);
  // START: GitHub Copilot Generated Code
  const hasContent = useAppSelector(selectHasContentForCurrentLanguage);
  // END: GitHub Copilot Generated Code
  // END: GitHub Copilot Generated Code
  useEffect(() => {
    const sessionLanguage = readFromStorage<string>(KEY_sessionLanguage, 'SessionStorage');
    const isRequestedLanguageSupported = isSupportedLanguage(lang);
    if (!isRequestedLanguageSupported && lang) {
      LogVerbose(`App: requested language ${lang} is not supported, falling back to session language or default.`);
    }
    const requestedLanguage = isRequestedLanguageSupported ? lang : null;
    const initialLanguage = requestedLanguage || sessionLanguage;
    const shouldSwitchLanguage = !!initialLanguage && initialLanguage !== currentLanguage;

    if (shouldSwitchLanguage) {
      LogVerbose(`App: restoring language ${initialLanguage} (lang prop takes precedence over session language).`);
      dispatch(setLanguage(initialLanguage));
    }
  }, [dispatch, currentLanguage, lang]);

  const isBrowserView = !isMobileApp();
  // START: GitHub Copilot Generated Code
  // Only block rendering when no content is available at all.
  // Local fallback content is loaded synchronously so this rarely triggers.
  // Once content exists (local or API), render the page even if
  // the API call is still in-flight — the page will re-render when
  // the API response arrives with the latest content.
  if (!hasContent) {
    return (
      <div className={`mymh-app ${!isBrowserView ? 'mobile-app' : ''}`} aria-busy="true" aria-label="Loading page content">
        <MyMhSpinner />
      </div>
    );
  }
  // END: GitHub Copilot Generated Code

  return (
    <div className={`mymh-app ${!isBrowserView ? 'mobile-app' : ''}`}>
      <Suspense fallback={<MyMhSpinner />}>
        <Routes>
          <Route path={config.urlConfig.root} element={<Home />} />
          <Route path={config.urlConfig.home} element={<Home fhirClient={fhirClient} />} />
          <Route path={config.urlConfig.patientHome} element={<Home fhirClient={fhirClient} />} />
          <Route path={config.urlConfig.gotoMychartLogin} element={
            <RedirectEffect onMount={() => {
              if (isBrowserView) {
                if (readFromStorage(KEY_debug) == 'true') {
                  toastError(`Current URL = ${window.location.href}; Redirecting to ${config.urlConfig.myChartLoginUrl}`);
                  setTimeout(() => redirectToUrl(config.urlConfig.myChartLoginUrl, 'replace'), 10000);
                }
                else
                  redirectToUrl(config.urlConfig.myChartLoginUrl, 'replace');
              }
              else {
                if (readFromStorage(KEY_debug) == 'true') {
                  toastError(`Current URL = ${window.location.href}; Redirecting to ${config.urlConfig.myChartLoginDeepLink}`);
                  setTimeout(() => redirectToUrl(config.urlConfig.myChartLoginDeepLink), 10000);
                }
                else
                  redirectToUrl(config.urlConfig.myChartLoginDeepLink);
              }
            }}>
              <Home fhirClient={fhirClient} />
            </RedirectEffect>
          } />
          <Route path={config.urlConfig.gotoMychartHome} element={
            <RedirectEffect onMount={() => {
              if (isBrowserView) {
                if (readFromStorage(KEY_debug) == 'true') {
                  toastError(`Current URL = ${window.location.href}; Redirecting to ${config.urlConfig.myChartHomeUrl}`);
                  setTimeout(() => redirectToUrl(config.urlConfig.myChartHomeUrl, 'replace'), 10000);
                }
                else
                  redirectToUrl(config.urlConfig.myChartHomeUrl, 'replace');
              }
              else {
                if (readFromStorage(KEY_debug) == 'true') {
                  toastError(`Current URL = ${window.location.href}; Redirecting to ${config.urlConfig.myChartHomeDeepLink}`);
                  setTimeout(() => redirectToUrl(config.urlConfig.myChartHomeDeepLink), 10000);
                }
                else
                  redirectToUrl(config.urlConfig.myChartHomeDeepLink);
              }
            }}>
              <Home fhirClient={fhirClient} />
            </RedirectEffect>
          } />
          <Route path={config.urlConfig.gotoTestResult} element={
            <RedirectEffect onMount={() => {
              if (isBrowserView)
                redirectToUrl(config.urlConfig.myChartTestResults, 'replace');
              else redirectToUrl(config.urlConfig.myChartTestResultsDeepLink);
            }}>
              <Home fhirClient={fhirClient} />
            </RedirectEffect>
          } />
          <Route path={config.urlConfig.payAsGuest} element={<PayAsGuest />} />
          <Route path={config.urlConfig.payBill} element={
            <RedirectEffect onMount={() => {
              if (isBrowserView)
                redirectToUrl(config.urlConfig.myChartPayBill, 'replace');
              else redirectToUrl(config.urlConfig.myChartPayBillDeeplink);
            }}>
              <Home fhirClient={fhirClient} />
            </RedirectEffect>
          } />

          <Route path={config.urlConfig.guestCostEstimate} element={
            <RedirectEffect onMount={() => {
              const url = addQueryParameter(config.urlConfig.myChartGuestCostEstimate, 'mobile', isBrowserView ? '0' : '1');
              redirectToUrl(url, 'replace');
            }}>
              <Home fhirClient={fhirClient} />
            </RedirectEffect>
          } />
          <Route path={config.urlConfig.costEstimate} element={
            <RedirectEffect onMount={() => {
              if (isBrowserView)
                redirectToUrl(config.urlConfig.myChartCostEstimate, 'replace');
              else redirectToUrl(config.urlConfig.myChartCostEstimateDeeplink);
            }}>
              <Home fhirClient={fhirClient} />
            </RedirectEffect>
          } />

          <Route path={config.urlConfig.getCareNow} element={<GetCareNow />} />
          <Route path={config.urlConfig.urgentCare} element={<UrgentCare />} />
          <Route path={config.urlConfig.virtualUrgentCare} element={<VirtualUrgentCare />} />
          <Route path={config.urlConfig.virtualWaitingRoom} element={<VirtualWaitingRoom />} />
          <Route path={config.urlConfig.scheduling} element={<Scheduling type='Scheduling' />} />
          <Route path={config.urlConfig.quickcareScheduling} element={<Scheduling type='UrgentScheduling' />} />
          <Route path={config.urlConfig.schedulingGuide} element={<SchedulingGuide category='GeneralScheduling' />} />
          <Route path={config.urlConfig.imageScheduling} element={<SchedulingGuide category='ImagingScheduling' />} />
          {/* <Route path={config.urlConfig.screeningScheduling} element={<SchedulingGuide category='ScreeningScheduling' />} /> */}
          {/* <Route path={config.urlConfig.therapyScheduling} element={<SchedulingGuide category='TherapyScheduling' />} /> */}
          <Route
            path={config.urlConfig.programsScheduling}
            element={
              <RedirectEffect onMount={() => redirectToUrl(config.urlConfig.mhProgramsAndClasses, 'replace')} />
            }
          />
          <Route path={config.urlConfig.tsBariatrics} element={<SchedulingGuide category='TicketScheduling' service='Bariatrics' />} />
          <Route path={config.urlConfig.tsCardiology} element={<SchedulingGuide category='TicketScheduling' service='Cardiology' />} />
          <Route path={config.urlConfig.tsEndocrinology} element={<SchedulingGuide category='TicketScheduling' service='Endocrinology' />} />
          <Route path={config.urlConfig.tsGastroenterology} element={<SchedulingGuide category='TicketScheduling' service='Gastroenterology' />} />
          <Route path={config.urlConfig.tsGeneralSurgery} element={<SchedulingGuide category='TicketScheduling' service='GeneralSurgery' />} />
          <Route path={config.urlConfig.tsNeurology} element={<SchedulingGuide category='TicketScheduling' service='Neurology' />} />
          <Route path={config.urlConfig.tsPediatricsWellChild} element={<SchedulingGuide category='TicketScheduling' service='PediatricsWellChild' />} />
          <Route path={config.urlConfig.tsPediatricsBHV} element={<SchedulingGuide category='TicketScheduling' service='PediatricsBHV' />} />
          <Route path={config.urlConfig.tsUrology} element={<SchedulingGuide category='TicketScheduling' service='Urology' />} />
          <Route path={config.urlConfig.tsFamilyMedicine} element={<SchedulingGuide category='TicketScheduling' service='FamilyMedicine' />} />
          <Route path={config.urlConfig.tsOBGYN} element={<SchedulingGuide category='TicketScheduling' service='OBGYN' />} />
          <Route path={config.urlConfig.tsAllergyImmunology} element={<SchedulingGuide category='TicketScheduling' service='AllergyImmunology' />} />
          <Route path={config.urlConfig.tsInfectiousDisease} element={<SchedulingGuide category='TicketScheduling' service='InfectiousDisease' />} />
          <Route path={config.urlConfig.tsRheumatology} element={<SchedulingGuide category='TicketScheduling' service='Rheumatology' />} />
          <Route path={config.urlConfig.functionIsComing} element={<FunctionIsComing />} />
          <Route path={config.urlConfig.visits} element={<ScheduledVisits />} />
          <Route
            path={config.urlConfig.supportRoute}
            element={<RedirectEffect onMount={() => redirectToUrl(config.urlConfig.supportUrl)} />}
          />
          <Route
            path={config.urlConfig.surveyRoute}
            element={<RedirectEffect onMount={() => redirectToUrl(config.urlConfig.surveyLink)} />}
          />
          <Route path={config.urlConfig.mealServiceRoute} element={<PlaceHolderPage title='Meal Service' message='This page is under construction.' />} />
          {config.env === "DEV" &&
            <Route path="/dev/iframe" element={<DevIFramePage iframeSrc="https://localhost:7279/authenticatedauth0/usermanagement/frontend" />} />}
          <Route path='*' element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
};


export default App;
